import { Injectable } from '@angular/core';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import { Observable, of } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ScanHeartService } from './scan-heart.service';
import { ScannerApiService } from '../scanner/scanner-api.service';
import { HrvInfoResult } from '../../interfaces/scanner-api/hrv-info-result';
import { HrvMeasurementResult } from '../../interfaces/biozoom-api/hrv-measurement-result';
import { State } from '../../interfaces/biozoom-api/app-configuration';
import { catchError, delay, filter, first, retryWhen, scan, switchMap, tap, timeout } from 'rxjs/operators';
import { BiozoomError } from '../../interfaces/scanner-api/biozoom-error';
import { FileManagerService } from '../fileManager/file-manager.service';
import { environment } from '../../../environments/environment';
import { getLogs } from '../../methods/send-logs';
import { NetworkService } from '../network.service';
import {checkScannerBattery} from '../../methods/helpers';
import {validateMeasurementAllowance} from '../../methods/measurement';
import {logError} from '../../methods/error';
import { getUploadHttpStatus, logUploadNetworkSummary } from '../../methods/upload-network-log';

@Injectable({
  providedIn: 'root'
})
export class ScanHeartObservablesService {

  constructor(
    private scanHeartService: ScanHeartService,
    private scannerApiService: ScannerApiService,
    private fileManagerService: FileManagerService,
    private networkService: NetworkService,
  ) { }

  private handleInsufficientCredits(pipelineConfig: PipelineStageConfig) {
    const onInsufficientCredits = pipelineConfig?.stageConfig?.eventTargets?.onInsufficientCredits;
    if (onInsufficientCredits) {
      pipelineConfig.executeStage(
        onInsufficientCredits,
        'onInsufficientCredits',
        pipelineConfig.stage
      );
    } else {
      pipelineConfig.showToast?.(
        'Your Scanner is licenced to be using coins, but your current config does not support this. Please contact customer support.',
        10000,
        'top'
      );
      pipelineConfig.executeStage(
        environment.appDesignSettings.homeStage ?? '',
        'onInsufficientCreditsMissingTarget',
        pipelineConfig.stage
      );
    }
    return of(true);
  }

  startMeasurementTest = (
    pipelineConfig: PipelineStageConfig,
  ) => {
    return switchMap((stati: StatusObject) => {
      // pipelineConfig.setStep({
      //   step: 'startMeasurement',
      //   heading: 'MEASUREMENT.H1_BREATH',
      // })
      const oldApi = stati._statusBefore.wifi_firmware?.startsWith('1.');
      const obs = this.scanHeartService.getMeasurementTest(
        pipelineConfig.state.persistentValues,
        oldApi,
      );
      return obs.pipe(
        // Will timeout after 30s.
        // This will no longer happen once the base-observable finishes.
        timeout(15000),
        switchMap((event: HttpEvent<any>) => {
          // TODO: Timeout here?
          switch (event.type) {
            case HttpEventType.Response: {
              pipelineConfig.setStep({
                hrvMeasurement: event.body,
              });
              return of(true);
              // return this.scannerApiService.getHrvMeasurementInfo(pipelineConfig.state.persistentValues);
            }
            case HttpEventType.UploadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
              };

              pipelineConfig.setStep(newSteps);
              return of<HrvInfoResult>(undefined);
            }
            case HttpEventType.DownloadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
              };
              pipelineConfig.setStep(newSteps);
              return of<HrvInfoResult>(undefined);
            }
            default: {
              return of<HrvInfoResult>(undefined);
            }
          }
        }),
        filter((el) => !!el),
        delay(1000),
        first(),
        tap((el) => {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
        }),
        catchError(e => {
          if (typeof e === 'object') {
            logError(e);
            if (e.name !== undefined && e.name === 'TimeoutError') {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                'onBadMeasurement',
                pipelineConfig.stage
              );
            }
          }
          return of(false);
        })
      );
    });
  }

  /**
   * @deprecated Legacy HRV test upload retained for existing configs.
   * New configs should use uploadDataHeartTestWithStatus.
   */
  uploadDataTest = (
    pipelineConfig: PipelineStageConfig,
  ) => {
    return switchMap((_stati: StatusObject) => {
      const measurement = pipelineConfig.getStep().hrvMeasurement;
      return this.scanHeartService.calculateMeasurementResultTest(
        pipelineConfig.getState(State.DEMO_MODE),
        pipelineConfig.getState(State.SELECTED_LANGUAGE),
        pipelineConfig.getStep().serial,
        measurement,
        pipelineConfig.getStep().info
      ).pipe(
        switchMap((res) => {
          if (res.nextPage === 'error_novalidlicense') {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoLicense, 'No license', pipelineConfig.stage);
            return of(true);
          }
          if (res.hand_drauf !== '1') {
            // pipelineConfig.handleError({type: 'hard', status: BiozoomError.DATA, restart: true});
            pipelineConfig.executeStage(
              pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
              'onBadMeasurement',
              pipelineConfig.stage
            );
            return of(true);
          } else {
            pipelineConfig.setStep({
              hrvDelete: true
            });
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return of(true);
          }
        })
      );
    });
  }

  /** HRV test upload with status reporting and a configurable network error target. */
  uploadDataTestWithStatus = (
    pipelineConfig: PipelineStageConfig,
  ) => {
    return switchMap((_stati: StatusObject) => {
      this.setHeartUploadStatus(pipelineConfig, {
        phase: 'testUpload',
        phases: { testUpload: 'active' },
        error: undefined,
      });

      const measurement = pipelineConfig.getStep().hrvMeasurement;
      return this.scanHeartService.calculateMeasurementResultTest(
        pipelineConfig.getState(State.DEMO_MODE),
        pipelineConfig.getState(State.SELECTED_LANGUAGE),
        pipelineConfig.getStep().serial,
        measurement,
        pipelineConfig.getStep().info
      ).pipe(
        switchMap((res) => {
          this.setHeartUploadPhase(pipelineConfig, 'testUpload', 'completed', { phase: 'testUpload' });
          if (res.nextPage === 'error_novalidlicense') {
            this.logHeartNetworkSummary(
              pipelineConfig, 'success', res.transportHttpStatus, undefined, 'testUpload',
              pipelineConfig?.stageConfig?.eventTargets?.onNoLicense,
            );
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoLicense, 'No license', pipelineConfig.stage);
            return of(true);
          }
          if (res.hand_drauf !== '1') {
            this.logHeartNetworkSummary(
              pipelineConfig, 'success', res.transportHttpStatus, undefined, 'testUpload',
              pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
            );
            pipelineConfig.executeStage(
              pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
              'onBadMeasurement',
              pipelineConfig.stage
            );
            return of(true);
          }
          pipelineConfig.setStep({ hrvDelete: true });
          this.logHeartNetworkSummary(
            pipelineConfig, 'success', res.transportHttpStatus, undefined, 'testUpload',
            pipelineConfig?.stageConfig?.eventTargets?.onSuccess,
          );
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
        }),
        catchError(error => {
          this.setHeartUploadPhase(pipelineConfig, 'testUpload', 'failed', {
            phase: 'failed',
            failedPhase: 'testUpload',
            error: this.heartUploadError(error),
          });
          this.logHeartNetworkSummary(
            pipelineConfig, 'failed', getUploadHttpStatus(error), error, 'testUpload',
            pipelineConfig?.stageConfig?.eventTargets?.onError,
          );
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
          return of(true);
        })
      );
    });
  }

  measureHeart = (
    pipelineConfig: PipelineStageConfig,
  ) => {
    return switchMap((stati: StatusObject) => {
      if (!validateMeasurementAllowance('hrv', pipelineConfig.state.persistentValues)) {
        return this.handleInsufficientCredits(pipelineConfig);
      }
      const obs = this.scanHeartService.getMeasurement(
        pipelineConfig.state.persistentValues,
      );
      return obs.pipe(
        // Will timeout after 30s.
        // This will no longer happen once the base-observable finishes.
        timeout(30000),
        switchMap((event: HttpEvent<any>) => {
          // TODO: Timeout here?
          switch (event.type) {
            case HttpEventType.Response: {
              pipelineConfig.setStep({
                hrvMeasurement: event.body,
                // idx: 0
              });
              // pipelineConfig.setState(State.AOX_MEASUREMENTS, pipelineConfig.getStep().measurements);
              // return this.scannerApiService.getHrvMeasurementInfo(pipelineConfig.state.persistentValues);
              return of(true);
            }
            case HttpEventType.UploadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
                // idx: 0,
              };
              // if (progress === 1) {
              //   newSteps.progress = 0;
              // }

              pipelineConfig.setStep(newSteps);
              // pipelineConfig.detectChanges();
              return of<HrvInfoResult>(undefined);
            }
            case HttpEventType.DownloadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
                // idx: 0
              };
              // if (offline && pipelineConfig.getStep().step !== 'calculateResult') {
              // }

              pipelineConfig.setStep(newSteps);
              return of<HrvInfoResult>(undefined);
            }
            default: {
              return of<HrvInfoResult>(undefined);
            }
          }
        }),
        filter((el) => !!el),
        delay(1000),
        first(),
        tap((el) => {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        }),
        catchError(e => {
          if (typeof e === 'object') {
            logError(e);
            if (e.name !== undefined && e.name === 'TimeoutError') {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                'onBadMeasurement',
                pipelineConfig.stage
              );
              return of(true);
            }
          }
          return of(false);
        })
      );
    });
  }

  initMeasurement = (
    pipelineConfig: PipelineStageConfig,
  ) => {
    if (pipelineConfig.state.persistentValues.SCANNER_VERSION === 2 && pipelineConfig.state.persistentValues.USB_MODE) {
      return this.measureHeart(pipelineConfig);
    }
    if (!validateMeasurementAllowance('hrv', pipelineConfig.state.persistentValues)) {
      return switchMap(() => {
        return this.handleInsufficientCredits(pipelineConfig);
      });
    }
    return switchMap((stati: StatusObject) => {
      const oldApi = stati._statusBefore.wifi_firmware?.startsWith('1.');
      return of({}).pipe(
        switchMap(async () => {
          await new Promise(r => setTimeout(r, 1000));
        }),
        switchMap(() => {
          if (oldApi) {
            return this.scanHeartService.getMeasurement(pipelineConfig.state.persistentValues);
          } else {
            return this.scanHeartService.initMeasurement(pipelineConfig.state.persistentValues);
          }
        }),

        // Will timeout after 30s.
        // This will no longer happen once the base-observable finishes.
        timeout(30000),
        switchMap((event: HttpEvent<any>) => {
          // @ts-ignore
          // TODO: Timeout here?
          switch (event.type) {
            case HttpEventType.Response: {
              pipelineConfig.setStep({
                hrvMeasurement: event.body,
              });
              return of<boolean>(true);
            }
            case HttpEventType.UploadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
                // idx: 0
              };
              pipelineConfig.setStep(newSteps);
              if (progress === 1 && !oldApi) {
                return of<boolean>(true);
              }
              return of<boolean>(false);
            }
            case HttpEventType.DownloadProgress: {
              const progress = event.loaded / event.total;
              const newSteps: any = {
                hrvProgress: progress,
                // idx: 0
              };
              pipelineConfig.setStep(newSteps);
              return of<boolean>(false);
            }
            default: {
              return of<boolean>(false);
            }
          }
        }),
        filter((el) => !!el),
        first(),
        tap(async (el) => {
          if (oldApi) {
            await this.scannerApiService.disconnectScanner(pipelineConfig.state.persistentValues);
            // await this.networkService.disconnectScanner(pipelineConfig.state.persistentValues);
          }
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        }),
        catchError(e => {
          if (typeof e === 'object') {
            logError(e);
            if (e.name !== undefined && e.name === 'TimeoutError') {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                'onBadMeasurement',
                pipelineConfig.stage
              );
              return of(true);
            }
          }
          return of(false);
        })
      );
    });
  }

  fetchMeasurement = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((stati: StatusObject) => {
      const oldApi = stati._statusBefore.wifi_firmware?.startsWith('1.');
      if (pipelineConfig.state.persistentValues.USB_MODE || oldApi || pipelineConfig.state.persistentValues.SCANNER_VERSION === 2) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }
      return this.scanHeartService.getMeasurementResult(
        pipelineConfig.state.persistentValues,
        pipelineConfig.state.persistentValues.SCANNER_VERSION === 2
      ).pipe(
        switchMap((event) => {
          if (event.type === HttpEventType.Response) {
            pipelineConfig.setStep({
              hrvMeasurement: event.body,
              // idx: 0
            });
            return of(true);
          } else {
            return of(false);
          }
        }),
        filter((e) => !!e),
        delay(1000),
        tap((e) => {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        }),
        catchError(e => {
          if (typeof e === 'object') {
            logError(e);
            if (e.name !== undefined && e.name === 'TimeoutError') {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                'onBadMeasurement',
                pipelineConfig.stage
              );
              return of(true);
            }
          }
          pipelineConfig.executeStage(
            pipelineConfig?.stageConfig?.eventTargets?.onScannerIssue,
            'onScannerIssue',
            pipelineConfig.stage
          );
          return of(true);
        })
      );
    });
  }

  /** Upload raw HRV data and retain the response for a later config stage. */
  uploadRawDataHeart = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((stati: StatusObject) => {
      const hrvMeasurement = pipelineConfig.getStep().hrvMeasurement;
      if (!hrvMeasurement) {
        return this.failHeartUpload(pipelineConfig, 'rawData', new Error('No HRV measurement available.'));
      }

      const context = {
        serial: pipelineConfig.getStep().serial,
        data: hrvMeasurement,
        additionalData: this.createHeartAdditionalData(pipelineConfig, stati),
        measurementType: pipelineConfig.state.temp?.measurementType,
        result: undefined,
      };
      if (!context.serial) {
        return this.failHeartUpload(pipelineConfig, 'rawData', new Error('No scanner serial available.'));
      }

      pipelineConfig.setStep({ hrvProgress: 0 });
      this.setHeartUploadState(pipelineConfig, context, {
        phase: 'rawDataSending',
        phases: this.createHeartUploadPhases(pipelineConfig, 'rawDataSending'),
        attempt: 1,
        maxRetries: this.getHeartUploadMaxRetries(pipelineConfig),
        retryPhase: undefined,
        failedPhase: undefined,
        error: undefined,
      });

      return this.scanHeartService.calculateMeasurementResultSingle(
        pipelineConfig.getState(State.DEMO_MODE),
        pipelineConfig.getState(State.SELECTED_LANGUAGE),
        context.serial,
        context.data,
        context.additionalData,
        context.measurementType,
      ).pipe(
        this.retryHeartUploadRequest(pipelineConfig, 'rawData'),
        switchMap((event: HttpEvent<HrvMeasurementResult>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              this.setHeartUploadStatus(pipelineConfig, { phase: 'rawDataSending' });
              return of(false);
            case HttpEventType.UploadProgress: {
              const progress = event.total ? event.loaded / event.total : 0;
              pipelineConfig.setStep({ hrvProgress: progress * 0.9 });
              if (event.total && event.loaded === event.total) {
                this.setHeartUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
                this.setHeartUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
                this.setHeartUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
              }
              return of(false);
            }
            case HttpEventType.DownloadProgress: {
              const progress = event.total ? event.loaded / event.total : 0;
              pipelineConfig.setStep({ hrvProgress: (progress * 0.1) + 0.9 });
              this.setHeartUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
              this.setHeartUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
              this.setHeartUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
              return of(false);
            }
            case HttpEventType.Response:
              context.result = event.body;
              this.setHeartUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
              this.setHeartUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
              this.setHeartUploadPhase(pipelineConfig, 'resultsRetrieving', 'completed');
              this.setHeartUploadState(pipelineConfig, context, {
                phase: 'resultRetrieved',
                phases: this.withHeartUploadPhase(pipelineConfig, 'resultRetrieved', 'completed'),
              });
              this.logHeartNetworkSummary(pipelineConfig, 'success', event.status);
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
              return of(true);
            default:
              return of(false);
          }
        }),
        filter(result => result),
        catchError(error => this.failHeartUpload(pipelineConfig, 'rawData', error))
      );
    });
  }

  /** Apply a retained HRV response using the same result rules as the legacy upload. */
  processUploadResultHeart = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((stati: StatusObject) => {
      const context = pipelineConfig.state?.temp?.heartUploadContext;
      if (!context?.result) {
        return this.failHeartUpload(pipelineConfig, 'resultProcessing', new Error('Missing HRV upload result.'));
      }
      this.setHeartUploadPhase(pipelineConfig, 'resultProcessing', 'active', { phase: 'resultProcessing' });
      return of(context.result).pipe(
        switchMap(async (result: HrvMeasurementResult) => this.applyHeartResult(pipelineConfig, stati, result)),
        tap(completed => {
          this.setHeartUploadPhase(pipelineConfig, 'resultProcessing', completed ? 'completed' : 'failed', {
            phase: completed ? 'completed' : 'failed',
            failedPhase: completed ? undefined : 'resultProcessing',
          });
          if (completed) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
        }),
        switchMap(() => of(true)),
        catchError(error => this.failHeartUpload(pipelineConfig, 'resultProcessing', error))
      );
    });
  }

  private createHeartAdditionalData = (pipelineConfig: PipelineStageConfig, stati: StatusObject) => {
    const profileData = pipelineConfig.getState(State.PROFILE_CHOSEN)
      ? pipelineConfig.getState(State.PROFILE_DATA)
      : pipelineConfig.getState(State.GUEST_DATA);
    let additionalData = {
      ...pipelineConfig.getStep().info,
      ...profileData,
      status_before: stati._statusBefore ?? {},
      status_after: stati._statusAfter ?? {},
      app_version: environment.appVersion,
      config: environment.appDesignName,
      localTimestamp: new Date(Date.now()).toString(),
      localLog: getLogs(),
    };
    if (pipelineConfig.state.additionalData) {
      additionalData = { ...additionalData, ...pipelineConfig.state.additionalData };
    }
    const aoxResult = pipelineConfig.state?.temp?.result;
    if (aoxResult?.precise_aox !== undefined && aoxResult?.precise_quality_result !== undefined) {
      additionalData.use_aox = aoxResult.precise_aox;
      additionalData.use_aox_q_precise = aoxResult.precise_quality_result;
    }
    if (pipelineConfig.state?.temp?.profile !== undefined) {
      const profile = pipelineConfig.state.temp.profile;
      additionalData = {
        ...additionalData,
        age: profile?.age,
        weight: profile?.weight,
        height: profile?.height,
        male: profile?.sex === 'male',
        female: profile?.sex === 'female',
        me: pipelineConfig.state.persistentValues?.profile?.isMe ?? false,
      };
    }
    return additionalData;
  }

  private retryHeartUploadRequest = (pipelineConfig: PipelineStageConfig, phase: string) => {
    return retryWhen(errors => errors.pipe(
      scan((attempt: number, error: any) => {
        const maxRetries = this.getHeartUploadMaxRetries(pipelineConfig);
        if (attempt >= maxRetries) {
          throw error;
        }
        this.setHeartUploadStatus(pipelineConfig, {
          phase: 'retrying',
          retryPhase: phase,
          attempt: attempt + 2,
          maxRetries,
          error: this.heartUploadError(error),
          phases: this.withHeartUploadPhase(pipelineConfig, 'rawDataSending', 'active'),
        });
        return attempt + 1;
      }, 0),
      delay(1000),
    ));
  }

  private getHeartUploadMaxRetries = (pipelineConfig: PipelineStageConfig): number => {
    const retries = Number(pipelineConfig.state?.persistentValues?.hrvUploadMaxRetries);
    return Number.isInteger(retries) && retries > 0 ? retries : 3;
  }

  private failHeartUpload = (pipelineConfig: PipelineStageConfig, phase: string, error: any): Observable<boolean> => {
    const workflowPhase = phase === 'resultProcessing' ? 'resultProcessing' : 'rawDataSending';
    this.setHeartUploadPhase(pipelineConfig, workflowPhase, 'failed', {
      phase: 'failed',
      failedPhase: phase,
      error: this.heartUploadError(error),
    });
    if (phase === 'rawData') {
      this.logHeartNetworkSummary(pipelineConfig, 'failed', getUploadHttpStatus(error), error);
    }
    pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
    return of(true);
  }

  private logHeartNetworkSummary = (
    pipelineConfig: PipelineStageConfig,
    outcome: 'success' | 'failed',
    httpStatus: number | null | undefined,
    error?: any,
    operation: 'rawDataUpload' | 'testUpload' = 'rawDataUpload',
    targetStage?: string,
  ) => {
    const status = pipelineConfig.state?.temp?.heartUploadStatus ?? {};
    const nextStage = targetStage ?? (outcome === 'success'
      ? pipelineConfig.stageConfig?.eventTargets?.onSuccess
      : pipelineConfig.stageConfig?.eventTargets?.onError);
    logUploadNetworkSummary(pipelineConfig, nextStage, {
      measurement: 'hrv',
      operation,
      outcome,
      attempt: status.attempt,
      maxRetries: status.maxRetries,
      httpStatus: httpStatus ?? null,
      ...(error ? {error: {name: error?.name, message: error?.message ?? String(error)}} : {}),
    });
  }

  private setHeartUploadState = (pipelineConfig: PipelineStageConfig, context: any, status: any) => {
    pipelineConfig.setState?.('heartUploadContext', context, 'temp');
    this.setHeartUploadStatus(pipelineConfig, status);
  }

  private setHeartUploadStatus = (pipelineConfig: PipelineStageConfig, update: any) => {
    const current = pipelineConfig.state?.temp?.heartUploadStatus ?? {};
    pipelineConfig.setState?.('heartUploadStatus', { ...current, ...update }, 'temp');
  }

  private createHeartUploadPhases = (pipelineConfig: PipelineStageConfig, activePhase: string) => {
    const phases = ['rawDataSending', 'rawDataSent', 'resultsRetrieving', 'resultRetrieved', 'resultProcessing'];
    const testUpload = pipelineConfig.state?.temp?.heartUploadStatus?.phases?.testUpload;
    return {
      ...(testUpload ? { testUpload } : {}),
      ...phases.reduce((result, phase) => ({ ...result, [phase]: phase === activePhase ? 'active' : 'pending' }), {}),
    };
  }

  private withHeartUploadPhase = (pipelineConfig: PipelineStageConfig, phase: string, state: string) => {
    const phases = pipelineConfig.state?.temp?.heartUploadStatus?.phases ?? this.createHeartUploadPhases(pipelineConfig, '');
    return { ...phases, [phase]: state };
  }

  private setHeartUploadPhase = (
    pipelineConfig: PipelineStageConfig, phase: string, state: string, update: any = {}
  ) => {
    this.setHeartUploadStatus(pipelineConfig, {
      ...update,
      phases: this.withHeartUploadPhase(pipelineConfig, phase, state),
    });
  }

  private heartUploadError = (error: any): string => {
    if (typeof error === 'string') {
      return error;
    }
    return error?.name ?? error?.message ?? 'unknown_error';
  }

  private async applyHeartResult(
    pipelineConfig: PipelineStageConfig, stati: StatusObject, res: HrvMeasurementResult
  ): Promise<boolean> {
    pipelineConfig.setStep({ hrvData: res });
    pipelineConfig.setState(State.HRV_MEASUREMENT_RESULT, res);
    pipelineConfig.setState('resultHrv', res, 'temp');
    this.fileManagerService.addToCsv(res, pipelineConfig, pipelineConfig.state?.temp?.resultAox, pipelineConfig.state.additionalDataStore ?? {});
    await pipelineConfig.setStep({ result: res });
    if (res.hrv_q < 50) {
      if (true || checkScannerBattery(stati._statusAfter, pipelineConfig.state.appSettings.batteryPrewarningVoltage)) {
        return true;
      }
      pipelineConfig.executeStage(
        pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? BiozoomError.BATTERY,
        'BatteryError', pipelineConfig.stage
      );
    } else if (res.hrv_q === 70) {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError70 ?? BiozoomError.ERROR_70, 'Error70', pipelineConfig.stage);
    } else if (res.hrv_q < 70) {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError60 ?? BiozoomError.LIGHT_60, 'Error60', pipelineConfig.stage);
    } else if (res.hrv_q < 60) {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError60 ?? BiozoomError.LIGHT_60, 'Error60', pipelineConfig.stage);
    } else {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'Error', pipelineConfig.stage);
    }
    return false;
  }

  /**
   * @deprecated Legacy single-stage Heart upload retained for existing configs.
   * New configs should use uploadRawDataHeart and processUploadResultHeart.
   */
  uploadData = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((stati: StatusObject) => {
      const hrvMeasurement = pipelineConfig.getStep().hrvMeasurement;
      let profileData;
      if (pipelineConfig.getState(State.PROFILE_CHOSEN)) {
        profileData = pipelineConfig.getState(State.PROFILE_DATA);
      } else {
        profileData = pipelineConfig.getState(State.GUEST_DATA);
      }

      // pipelineConfig.setState(State.HRV_MEASUREMENT_RUNNING, false);

      let additionalData = {
        ...pipelineConfig.getStep().info,
        ...profileData,
        status_before: stati._statusBefore ?? {},
        status_after: stati._statusAfter ?? {},
        app_version: environment.appVersion,
        config: environment.appDesignName,
        localTimestamp: new Date(Date.now()).toString(),
        localLog: getLogs(),
      };
      if (pipelineConfig.state.additionalData) {
        additionalData = { ...additionalData, ...pipelineConfig.state.additionalData };
      }

      const res = pipelineConfig.state?.temp?.result;
      if (res) {
        const precise_aox = res?.precise_aox;
        const precise_quality_result = res?.precise_quality_result;
        if (precise_aox !== undefined && precise_quality_result !== undefined) {
          additionalData.use_aox = precise_aox;
          additionalData.use_aox_q_precise = precise_quality_result;
        }
      }

      if (pipelineConfig.state?.temp?.profile !== undefined) {
        const age = pipelineConfig.state.temp.profile?.age ?? undefined;
        const weight = pipelineConfig.state.temp.profile?.weight ?? undefined;
        const height = pipelineConfig.state.temp.profile?.height ?? undefined;
        const sex = pipelineConfig.state.temp.profile?.sex ?? undefined;
        const me = pipelineConfig.state.persistentValues?.profile?.isMe ?? false;
        const profile = {
          age,
          weight,
          height,
          male: sex === 'male',
          female: sex === 'female',
          me,
        };
        additionalData = { ...additionalData, ...profile };
      }

      return this.scanHeartService.calculateMeasurementResult(
        pipelineConfig.getState(State.DEMO_MODE),
        pipelineConfig.getState(State.SELECTED_LANGUAGE),
        pipelineConfig.getStep().serial,
        hrvMeasurement,
        additionalData,
        pipelineConfig.state.temp?.measurementType
      ).pipe(
        switchMap(async (event: any) => {
          if ((event as HttpEvent<any>).type === undefined) {
            return [false, false];
          } else {
            event = event as HttpEvent<HrvMeasurementResult>;
            switch (event.type) {
              case HttpEventType.Response: {
                const res: HrvMeasurementResult = event.body;
                pipelineConfig.setStep({
                  hrvData: res
                });
                pipelineConfig.setState(State.HRV_MEASUREMENT_RESULT, res);
                pipelineConfig.setState('resultHrv', res, 'temp');
                this.fileManagerService.addToCsv(res, pipelineConfig, pipelineConfig.state?.temp?.resultAox, pipelineConfig.state.additionalDataStore ?? {});

                await pipelineConfig.setStep({
                  result: res
                });
                if (res.hrv_q < 50) {
                  if (true || checkScannerBattery(stati._statusAfter, pipelineConfig.state.appSettings.batteryPrewarningVoltage)) {
                    // return of(true);
                    return [true, true];
                  } else {
                    pipelineConfig.executeStage(
                      pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? BiozoomError.BATTERY,
                      'BatteryError',
                      pipelineConfig.stage
                    );
                  }
                  // pipelineConfig.setState(State.HRV_MEASUREMENT_RESULT, hrv);
                  // }
                } else if (res.hrv_q === 70) {
                  pipelineConfig.executeStage(
                    pipelineConfig?.stageConfig?.eventTargets?.onError70 ?? BiozoomError.ERROR_70,
                    'Error70',
                    pipelineConfig.stage
                  );
                } else if (res.hrv_q < 70) {
                  pipelineConfig.executeStage(
                    pipelineConfig?.stageConfig?.eventTargets?.onError60 ?? BiozoomError.LIGHT_60,
                    'Error60',
                    pipelineConfig.stage
                  );
                } else if (res.hrv_q < 60) {
                  pipelineConfig.executeStage(
                    pipelineConfig?.stageConfig?.eventTargets?.onError60 ?? BiozoomError.LIGHT_60,
                    'Error60',
                    pipelineConfig.stage
                  );
                } else {
                  pipelineConfig.executeStage(
                    pipelineConfig?.stageConfig?.eventTargets?.onError,
                    'Error',
                    pipelineConfig.stage
                  );
                }
                return [true, false];
              }
              case HttpEventType.DownloadProgress:
              case HttpEventType.UploadProgress: {
                const progress = event.loaded / event.total;
                pipelineConfig.setStep({
                  hrvProgress: event.type === HttpEventType.UploadProgress ? (progress * 0.9) : (progress * 0.1) + 0.9
                });
                break;
              }
              default: {
                return [false, false];

              }
            }
          }
          return [false, false];
        }),
        filter(el => !!el[0]),
        tap((el) => {
          if (el[1]) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
        })
      );
    });
  }
}
